/**
 * Default config for the Enquiry Ingest preview page.
 *
 * The page lets a developer iterate on a prompt + JSON schema for parsing a
 * raw enquiry email into structured fields. Edits to anything here are stored
 * in localStorage so refreshes don't lose work; the "Reset to defaults" button
 * re-seeds from these constants.
 *
 * The structured output schema is *built dynamically* from the user's field
 * definitions in the UI — these defaults just give a sensible starting point.
 */

import { AI_MODELS, PLACEHOLDER_VARIABLES } from "../communications/_components/ai-tools/registry"

export { AI_MODELS, PLACEHOLDER_VARIABLES }

export const DEFAULT_MODEL = "gpt-4.1-mini"
export const DEFAULT_TEMPERATURE = 0

export const FIELD_TYPES = [
	"string",
	"number",
	"integer",
	"boolean",
	"date",
	"array_string",
]

/**
 * Seed list of fields we'd like the model to populate from a typical inbound
 * enquiry. Each field becomes a property in the dynamically-built JSON schema
 * sent to OpenAI. `description` is fed to the model — keep it specific and
 * grounded in what the parser should look for.
 */
export const DEFAULT_FIELDS = [
	{
		key: "lead_first_name",
		label: "Lead first name",
		type: "string",
		description:
			"First name of the prospective customer making the enquiry. Take the most explicit signature/name in the body, falling back to the From header name.",
		required: true,
	},
	{
		key: "lead_last_name",
		label: "Lead last name",
		type: "string",
		description:
			"Last name (surname) of the prospective customer. Take the most explicit signature/name in the body, falling back to the From header name. Null if no surname is given.",
		required: false,
	},
	{
		key: "lead_email",
		label: "Lead email",
		type: "string",
		description: "Best reply-to email address for the lead.",
		required: false,
	},
	{
		key: "lead_phone",
		label: "Lead phone",
		type: "string",
		description: "Phone number for the lead, including international prefix when present.",
		required: false,
	},
	{
		key: "group_size",
		label: "Group size",
		type: "integer",
		description:
			"Total number of travellers in the group (golfers + non-golfers). Null if the email doesn't say.",
		required: false,
	},
	{
		key: "golfer_count",
		label: "Golfer count",
		type: "integer",
		description: "Number of golfers in the group. Null if not stated.",
		required: false,
	},
	{
		key: "non_golfer_count",
		label: "Non-golfer count",
		type: "integer",
		description: "Number of non-golfers in the group. Null if not stated.",
		required: false,
	},
	{
		key: "destination",
		label: "Destination",
		type: "string",
		description:
			"Requested destination — a country, region, or specific town. Take the most specific reference (e.g. 'Vilamoura' over 'Algarve'). Null if unspecified.",
		required: false,
	},
	{
		key: "preferred_hotels",
		label: "Preferred hotels",
		type: "array_string",
		description:
			"Specific hotels or resorts the lead has named or asked for. Empty array if none mentioned.",
		required: false,
	},
	{
		key: "accommodation_type",
		label: "Accommodation preference",
		type: "string",
		description:
			"Preference between hotel, villa, apartment, or similar. Null if not stated.",
		required: false,
	},
	{
		key: "travel_start_date",
		label: "Travel start date",
		type: "date",
		description:
			"Earliest travel/arrival date in ISO YYYY-MM-DD format. Resolve relative dates like 'next May' against the email's received date when present.",
		required: false,
	},
	{
		key: "trip_length_nights",
		label: "Trip length (nights)",
		type: "integer",
		description: "Length of trip in nights. Null if not stated.",
		required: false,
	},
	{
		key: "rounds_of_golf",
		label: "Rounds of golf",
		type: "integer",
		description: "Number of rounds of golf requested across the trip. Null if not stated.",
		required: false,
	},
	{
		key: "preferred_courses",
		label: "Preferred courses",
		type: "array_string",
		description:
			"Specific golf courses the lead has named or asked for. Empty array if none mentioned.",
		required: false,
	},
	{
		key: "budget_per_person",
		label: "Budget per person",
		type: "number",
		description:
			"Budget per person in GBP if explicitly stated. Convert other currencies only if a clear figure is given. Null if not stated.",
		required: false,
	},
	{
		key: "client_message",
		label: "Client message",
		type: "string",
		description:
			"The exact original message from the client, copied verbatim with no rewording, summarising, or formatting changes. Preserve the lead's own wording, punctuation, and line breaks. Exclude email signatures, quoted history from prior replies, automatic disclaimers, and forwarded headers — keep only the message text the lead actually wrote in this email. Null if there is no client-authored body (e.g. the email is just a forwarded attachment).",
		required: false,
	},
	{
		key: "summary",
		label: "Summary",
		type: "string",
		description:
			"Concise summary of the enquiry — the trip the lead is asking about and any context, special requests, or constraints that don't fit the structured fields above. One short paragraph, written in your own words. Do not invent details that aren't in the email.",
		required: false,
	},
]

/**
 * UTM parameters extracted from the email body. Fixed list (the five Google
 * Analytics standard params) — any URL or text mention of these in the body
 * should populate the matching key. Always present in the response shape;
 * each value defaults to null when not found.
 */
export const UTM_KEYS = [
	{ key: "utm_source", label: "Source" },
	{ key: "utm_medium", label: "Medium" },
	{ key: "utm_campaign", label: "Campaign" },
	{ key: "utm_term", label: "Term" },
	{ key: "utm_content", label: "Content" },
]

/**
 * Client signals — standardised, low-cardinality device/browser hints parsed
 * out of any User-Agent string or equivalent the email exposes. Each field is
 * constrained to an enum (or boolean) so values stay stable for aggregate
 * reporting later (e.g. "mobile leads convert 10× better than desktop").
 *
 * Versions are deliberately omitted in v1 — they're high-cardinality and hard
 * to aggregate on without bucketing logic. Add them when there's a concrete
 * report that needs them.
 */
export const CLIENT_SIGNAL_FIELDS = [
	{
		key: "user_agent_raw",
		label: "User-Agent (raw)",
		type: "string",
		description:
			"Full User-Agent string from the lead's browser, copied verbatim if the email body contains one. Preserved as-is for forensic/debug use; the parsed enums below are what's used for reporting. Null if no UA string is present in the email.",
	},
	{
		key: "device_type",
		label: "Device type",
		enum: ["mobile", "tablet", "desktop", "bot", "unknown"],
		description:
			"Form factor of the device the enquiry was sent from. Use 'unknown' when the email doesn't expose any client hints; use 'bot' when the User-Agent or context indicates an automated/crawler client.",
	},
	{
		key: "operating_system",
		label: "Operating system",
		enum: ["ios", "android", "windows", "macos", "linux", "chromeos", "other"],
		description:
			"Operating system inferred from the User-Agent or any other client hint in the email. Use 'other' for anything outside the listed values.",
	},
	{
		key: "browser",
		label: "Browser",
		enum: [
			"chrome",
			"safari",
			"firefox",
			"edge",
			"opera",
			"samsung_internet",
			"brave",
			"other",
		],
		description:
			"Browser inferred from the User-Agent. Use the family name, not the rendering engine (e.g. Edge → 'edge', not 'chromium'). Use 'other' for anything outside the listed values.",
	},
	{
		key: "is_bot",
		label: "Is bot",
		type: "boolean",
		description:
			"True when the User-Agent or context strongly indicates the enquiry was submitted by an automated/crawler client rather than a human.",
	},
	{
		key: "timezone",
		label: "Timezone",
		type: "string",
		description:
			"IANA timezone identifier captured from the lead's browser when the enquiry was submitted (e.g. 'Europe/London', 'America/New_York'). Copied verbatim from the email body if present. Null if the email doesn't include a timezone signal.",
	},
	{
		key: "local_time",
		label: "Local time of enquiry",
		type: "string",
		description:
			"ISO 8601 datetime in the lead's *local* time (with offset) at the moment they submitted the enquiry — e.g. '2026-05-05T14:32:00+01:00'. Copied verbatim from the email body if present. Used for hour-of-day reporting. Null if the email doesn't include a local timestamp.",
	},
]

/**
 * Date signals — derived/inferred timing context that helps sales agents
 * triage immediately. Fixed shape with enum-constrained categories so the
 * fields stay stable for aggregate reporting (e.g. "stag groups in the
 * `short_lead` window convert at X%"). Counts and dates are extracted/
 * computed from the email content + the email's received timestamp
 * ({{occurred_at}}).
 */
export const DATE_SIGNAL_FIELDS = [
	{
		key: "travel_month",
		label: "Travel month",
		type: "integer",
		description:
			"Month of the trip start as 1–12 (1 = January). Resolve from travel_start_date or any month named in the body. Null if unknown.",
	},
	{
		key: "travel_year",
		label: "Travel year",
		type: "integer",
		description:
			"Four-digit year of the trip start (e.g. 2026). Resolve from travel_start_date or any year named in the body, falling back to context (e.g. 'next May' relative to the email's received date). Null if unknown.",
	},
	{
		key: "days_until_travel",
		label: "Days until travel",
		type: "integer",
		description:
			"Whole-day count from the email's received date ({{occurred_at}}) to the travel start date. Null if either side is unknown. May be negative if the trip start is in the past relative to the email.",
	},
	{
		key: "booking_window_category",
		label: "Booking window",
		enum: ["last_minute", "short_lead", "normal", "long_lead", "unknown"],
		description:
			"Categorical bucket for how soon the trip is, derived from days_until_travel. Use 'last_minute' for ≤14 days, 'short_lead' for 15–60 days, 'normal' for 61–180 days, 'long_lead' for >180 days. Use 'unknown' when days_until_travel is null.",
	},
	{
		key: "date_flexibility",
		label: "Date flexibility",
		enum: ["fixed_dates", "flexible_month", "flexible_season", "unknown"],
		description:
			"How firm the lead's dates are. 'fixed_dates' = specific arrival/return given with no flex language; 'flexible_month' = a specific month but flexible within it ('any week in May'); 'flexible_season' = a season/quarter only ('next spring', 'autumn 2026'); 'unknown' = no date signal at all.",
	},
	{
		key: "mentioned_flexible_dates",
		label: "Mentioned flexible dates",
		type: "boolean",
		description:
			"True if the lead explicitly says they're flexible on dates ('happy to move dates', 'open to other weeks', 'whatever works'). Distinct from date_flexibility — this is purely about explicit language.",
	},
	{
		key: "peak_period",
		label: "Peak period",
		type: "boolean",
		description:
			"True if the requested travel falls in a known peak golf-travel period — broadly Apr–Jun and Sep–Oct in the Algarve/Spain/Portugal corridor, school holiday weeks anywhere in the UK, and major event weeks (Masters week in April, Open week in July). False otherwise; false also when travel month is unknown.",
	},
	{
		key: "weekend_trip",
		label: "Weekend trip",
		type: "boolean",
		description:
			"True if the requested trip clearly spans a weekend pattern (Thu–Sun, Fri–Mon, or trip_length_nights ≤ 4 with start day on Thu/Fri). False otherwise; false when dates and length are unknown.",
	},
]

/**
 * Group signals — what *kind* of group this is, beyond the raw counts. Same
 * fixed-shape pattern as date_signals so the categorical fields stay
 * reportable. Sales pitches differ a lot between, say, a stag group and a
 * golf society, so these drive routing as much as triage.
 */
export const GROUP_SIGNAL_FIELDS = [
	{
		key: "trip_type",
		label: "Trip type",
		enum: [
			"golf_break",
			"society_trip",
			"stag_do",
			"couples_trip",
			"corporate",
			"family",
			"solo_or_pair",
			"unknown",
		],
		description:
			"Best fit for the overall purpose of the trip. 'golf_break' is the catch-all for casual golf groups; 'society_trip' is an organised golf society outing; 'stag_do' is a stag/bachelor party; 'couples_trip' is one or more couples travelling together; 'corporate' is a company/client trip; 'family' includes children or multiple generations; 'solo_or_pair' is one or two travellers with no wider group context.",
	},
	{
		key: "occasion",
		label: "Occasion",
		enum: ["birthday", "stag", "annual_trip", "corporate_event", "unknown"],
		description:
			"Specific occasion driving the trip when the lead names one. 'unknown' when no occasion is mentioned.",
	},
	{
		key: "party_profile",
		label: "Party profile",
		enum: [
			"lads_group",
			"mixed_group",
			"couples",
			"senior_golfers",
			"corporate",
			"unknown",
		],
		description:
			"Best fit for who's in the party. 'lads_group' = all-male casual group; 'mixed_group' = mixed gender / mixed golfers and non-golfers; 'couples' = paired travellers; 'senior_golfers' = retired-age golf group; 'corporate' = colleagues/clients; 'unknown' when there's no signal.",
	},
	{
		key: "decision_maker_count",
		label: "Decision-maker count",
		type: "integer",
		description:
			"Number of people who appear to be involved in the booking decision (often the organiser plus 1–2 'I'll need to check with...' references). Null if there's no signal. Useful for predicting how slow a decision will be.",
	},
	{
		key: "single_organiser_detected",
		label: "Single organiser detected",
		type: "boolean",
		description:
			"True when one person is clearly organising on behalf of the group (uses 'I'll book for the group', 'they've asked me to enquire', signs off without consulting language). False when the lead is enquiring just for themselves or when there's no signal.",
	},
	{
		key: "requires_non_golfer_options",
		label: "Requires non-golfer options",
		type: "boolean",
		description:
			"True when the email mentions non-golfers in the party who'll need spa/pool/excursion options, or asks for activities outside the course. False otherwise.",
	},
	{
		key: "likely_stag_or_celebration",
		label: "Likely stag or celebration",
		type: "boolean",
		description:
			"True when language strongly indicates a stag, milestone birthday, or celebration trip ('stag', 'bachelor', '40th', 'last hurrah', references to night-life). False otherwise. Distinct from trip_type/occasion — captures the vibe even when not explicitly labelled.",
	},
]

/**
 * Predictions are 0–1 floats the model is asked to estimate alongside the
 * structured fields. They live under a nested `predictions` object in the
 * schema so they're cleanly separable from the extracted facts. Use them for
 * triage signals (intent, urgency, buyer-readiness) — not for things you
 * could deterministically derive from the parsed fields.
 */
export const DEFAULT_PREDICTIONS = [
	{
		key: "intent",
		label: "Enquiry intent",
		description:
			"How strongly the email reads as a genuine enquiry from a prospective customer about a golf trip, vs. noise (marketing, automated reply, internal forward, support question, spam). 1 = unambiguous enquiry; 0 = clearly not an enquiry.",
	},
	{
		key: "extraction_confidence",
		label: "Extraction confidence",
		description:
			"Overall confidence that the structured fields above were extracted correctly. Reflects how much was explicitly stated vs. inferred. 1 = every populated field is directly stated; lower as more fields rely on inference or are left null.",
	},
	{
		key: "buyer_readiness",
		label: "Buyer readiness",
		description:
			"How ready the lead seems to book. 1 = ready to confirm/pay this week; 0 = early research, no real commitment. Look for concrete dates, party size, budget, decision language ('we want to book', 'please send a quote') vs. browsing language ('just exploring', 'thinking about').",
	},
	{
		key: "urgency",
		label: "Urgency",
		description:
			"Time pressure to respond. 1 = explicitly urgent or travelling very soon; 0 = no time pressure indicated. Consider stated travel dates relative to received date, language ('ASAP', 'urgent', 'by end of week'), and whether the lead is mid-decision.",
	},
	{
		key: "specificity",
		label: "Specificity",
		description:
			"How concrete the requirements are. 1 = specific destination, dates, party size, course preferences, budget; 0 = vague ('somewhere sunny', 'sometime next year'). Higher specificity makes it easier to quote without follow-up.",
	},
	{
		key: "group_organiser_likelihood",
		label: "Group organiser likelihood",
		description:
			"Likelihood the sender is organising on behalf of a wider group rather than enquiring just for themselves. 1 = clearly organising for others (uses 'the group', names other travellers, asks for per-person pricing); 0 = clearly a solo or couple enquiry.",
	},
	{
		key: "price_sensitivity",
		label: "Price sensitivity",
		description:
			"How much the email emphasises budget, value, or cost. 1 = very price-led ('cheapest', 'tight budget', explicit cap); 0 = no mention of price or premium-led language.",
	},
	{
		key: "complexity",
		label: "Trip complexity",
		description:
			"How complex the requested trip is to quote. 1 = multi-destination, mixed party (golfers + non-golfers + kids), special requests, transfers between resorts; 0 = single hotel, single course, simple party. Helps route to senior agents when high.",
	},
	{
		key: "spam_or_test_likelihood",
		label: "Spam / test likelihood",
		description:
			"Likelihood the email is spam, a test submission, or otherwise not a real lead. 1 = obviously fake/test/spam; 0 = clearly genuine. Look for placeholder names ('asdf', 'test'), nonsense bodies, mismatched form fields, repeated submissions.",
	},
]

export const DEFAULT_SYSTEM_INSTRUCTIONS = `# Role

You are an enquiry parser for Golf Holidays Direct, a UK golf-holiday tour operator. Your job is to extract structured details from an inbound enquiry email so the sales team can quickly triage and quote the trip.

# Method

1. Read the entire email body carefully, including any forwarded or quoted history.
2. Populate every field defined in the JSON schema. Required fields must always be filled; for optional fields, return null (or an empty array for list types) if the email genuinely doesn't say.
3. Prefer specific over generic — "Vilamoura" beats "Algarve", "Quinta do Lago" beats "luxury resort".
4. Resolve relative dates ("next May", "first week of October") against the received timestamp ({{occurred_at}}) when one is provided. If you can't resolve to an exact date, return null rather than guessing.
5. Never invent information that isn't in the email. If you're unsure, leave the field null.
6. For free-text fields like \`additional_notes\`, summarise concisely — don't repeat structured detail already captured elsewhere.

# Field formatting

Normalise extracted values so the CRM doesn't have to clean them up afterwards. Apply these rules consistently regardless of how the email writes them:

**Names** (\`lead_first_name\`, \`lead_last_name\`, and any other name-shaped string):

- Title-case: capitalise the first letter, lowercase the rest, applied to each word in the name.
- Preserve the internal capitalisation of compound surnames and prefixes:
  - \`Mc\` and \`Mac\` keep the next letter capitalised: \`McCurley\`, \`McGrath\`, \`McDonald\`, \`MacIntyre\`, \`MacDonald\`. Do NOT lower-case to \`Mccurley\`/\`Mcgrath\`.
  - \`O'\` keeps the next letter capitalised: \`O'Brien\`, \`O'Connor\`, \`O'Reilly\`.
  - Particles like \`van\`, \`van der\`, \`de\`, \`de la\`, \`du\`, \`von\`, \`del\`, \`della\` stay lowercase when they appear mid-name (e.g. \`Pieter van der Berg\`, \`Marco della Valle\`), but capitalise when they're the leading word (\`Van der Berg\` if used as a surname on its own).
  - Hyphenated names: capitalise after the hyphen too (\`Mary-Anne\`, \`Smith-Jones\`).
  - Apostrophes mid-word other than \`O'\`: capitalise after the apostrophe (\`D'Angelo\`).
- If the source has all-caps or all-lowercase ("john SMITH" or "JOHN SMITH"), still output title-cased: \`John Smith\`.
- Preserve correct accents (\`José\`, \`Renée\`).

**Email addresses** (\`lead_email\` and any email-shaped string):

- Always entirely lowercase: \`John.Smith@Example.COM\` → \`john.smith@example.com\`.
- Strip any surrounding angle brackets, quotes, or whitespace.

**Phone numbers** (\`lead_phone\`):

- Preserve the lead's own formatting (don't reformat \`+44 7700 900 123\` into \`+447700900123\`); only trim leading/trailing whitespace.
- If the number is already internationalised (starts with \`+\` or \`00\`), leave it exactly as written.
- If the number is in clearly national/local form (e.g. UK \`07700 900123\` or \`7700 900123\`, IE \`087 1234567\`, US \`(415) 555-0132\`) AND \`client_signals.timezone\` maps unambiguously to a single country, prepend the matching dialing code with a leading \`+\` and a space, preserving the rest of the lead's formatting. Examples: timezone \`Europe/London\` + \`07700 900123\` → \`+44 7700 900123\`; timezone \`Europe/Dublin\` + \`087 1234567\` → \`+353 87 1234567\`; timezone \`America/Los_Angeles\` + \`(415) 555-0132\` → \`+1 (415) 555-0132\`.
  - Drop a single trunk prefix when prepending the international code (UK/IE leading \`0\`). Don't otherwise restructure the number.
  - If the timezone is missing, ambiguous across multiple countries (e.g. \`Europe/Zurich\` covers CH/LI; many \`America/*\` zones span US and Canada — treat \`+1\` as the shared NANP code and prepend it), or the number doesn't look like a valid national format for the inferred country, leave the number untouched.

# UTM tracking

Populate the \`utm_tracking\` object with any of the five standard UTM parameters (\`utm_source\`, \`utm_medium\`, \`utm_campaign\`, \`utm_term\`, \`utm_content\`) you can find anywhere in the email — typically in URLs in the body, in signature links, in tracked CTAs, or sometimes pasted as plain text. Match parameters by name, case-insensitive. If a parameter is not present, leave it as null. Do not invent values; only populate what's actually written.

# Client signals

Populate the \`client_signals\` object from any User-Agent string, device hint, or app footer the email exposes (lead-capture forms often append a UA line, signature, or "Sent from my iPhone"-style hint). Use the standardised enum values exactly as listed in the schema — these feed aggregate reporting, so spelling and casing matter.

Rules:

- \`user_agent_raw\` — copy the full User-Agent string verbatim if one appears in the email body. No editing, no truncation. Null if no UA string is present.
- \`device_type\` / \`operating_system\` / \`browser\` — parse from the UA string or any device hint in the body. Match by recognisable signals: the literal token "iPhone"/"iPad" → \`ios\`; "Android" → \`android\`; "Macintosh"/"Mac OS X" → \`macos\`; "Windows" → \`windows\`; etc. For browsers, prefer the family name in the UA's product token (\`Chrome/\`, \`Safari/\`, \`Edg/\`, \`Firefox/\`, \`OPR/\`, \`SamsungBrowser/\`). Edge UA strings contain both "Chrome" and "Edg" — choose \`edge\`. Brave is detectable via the \`Brave\` token or Sec-CH-UA.
- "Sent from my iPhone" / "Sent from my Android" footers count as device hints — use them to set \`device_type\` and \`operating_system\` even if no full UA is present.
- \`is_bot\` — set to \`true\` only when the UA or context clearly indicates a crawler/automated client.
- \`timezone\` and \`local_time\` — these are pass-through values the source application embeds in the email body when capturing the enquiry. Copy them verbatim if present (\`timezone\` as the IANA identifier, \`local_time\` as an ISO 8601 datetime with offset). Do not infer them from anything else — if they're not explicitly in the body, leave them null.
- If no signals are present at all, leave each field as \`null\` (or \`is_bot: false\`). Do not guess from unrelated context.

# Date signals

Populate the \`date_signals\` object with travel-timing context derived from the structured dates above and the email's received timestamp ({{occurred_at}}).

Rules:

- \`travel_month\` (1–12) and \`travel_year\` (4-digit) come from \`travel_start_date\` if it's set, otherwise from any month/year named in the body. Resolve relative phrases ('next May') against {{occurred_at}}. Null when truly unknown.
- \`days_until_travel\` is the whole-day gap from {{occurred_at}} to \`travel_start_date\`. Null when either side is unknown. Negative is allowed (trip start in the past).
- \`booking_window_category\` follows from \`days_until_travel\`: ≤14 → \`last_minute\`, 15–60 → \`short_lead\`, 61–180 → \`normal\`, >180 → \`long_lead\`. \`unknown\` when \`days_until_travel\` is null.
- \`date_flexibility\` reflects how firm the dates are: explicit specific dates with no flex language → \`fixed_dates\`; specific month with flex inside it → \`flexible_month\`; only a season/quarter named → \`flexible_season\`; no date signal at all → \`unknown\`.
- \`mentioned_flexible_dates\` is purely about explicit language ("happy to move", "open to other weeks"). Independent of \`date_flexibility\`.
- \`peak_period\` flags known busy windows for golf travel (broadly Apr–Jun and Sep–Oct in the Algarve/Spain/Portugal corridor, UK school holiday weeks, Masters week, Open week). False when month is unknown.
- \`weekend_trip\` flags short trips that fit a weekend pattern (Thu–Sun, Fri–Mon, or trip_length_nights ≤ 4 starting Thu/Fri).

# Group signals

Populate the \`group_signals\` object with information about *who* the group is and *why* they're travelling, beyond the raw counts.

Rules:

- \`trip_type\` and \`party_profile\` are best-fit categorisations — pick the closest match. Use \`unknown\` only when there's genuinely no signal.
- \`occasion\` only fires when an occasion is explicitly named ('stag', 'birthday trip', 'annual'). \`unknown\` otherwise.
- \`decision_maker_count\` is your read on how many people will need to agree before this books — typically the organiser plus anyone they'll need to "check with". Null when no signal.
- \`single_organiser_detected\` — true when one person is clearly enquiring on behalf of the group (uses 'they want me to', 'I'm sorting it for the lads'). False when the lead is enquiring just for themselves or it's unclear.
- \`requires_non_golfer_options\` — true when non-golfers are mentioned and will need things to do (spa, pool, excursions). False when not mentioned.
- \`likely_stag_or_celebration\` — true when the language reads like a stag, milestone birthday, or celebration trip even if not explicitly labelled. Catches the 'lads' vibe even when the word "stag" isn't used. False otherwise.

# Predictions

Alongside the structured fields, populate the \`predictions\` object. Each prediction is a float between 0 and 1 — read each prediction's description carefully and calibrate against it. Predictions are a separate, *judgement* layer; they don't have to match what's in the structured fields, and they shouldn't try to fill in for missing facts.

Calibration tips:

- Use the full 0–1 range. Default to mid-range (~0.4–0.6) when genuinely unsure rather than always reaching for 0 or 1.
- Treat each prediction independently. Urgency and buyer_readiness can disagree.
- \`extraction_confidence\` should drop when many fields had to be inferred or left null, even if the email itself looked like a clear enquiry.

# Tone

Be conservative on field extraction. A null is always better than a fabricated value. The sales team will reach out to clarify gaps; they cannot easily catch a hallucinated date or budget. Predictions are allowed to be opinionated — that's their job.`

export const DEFAULT_USER_INSTRUCTIONS = `Subject: {{subject}}
From: {{from}} (domain: {{from_domain}})
Received: {{occurred_at}}

Structural signals:
- has_unsubscribe: {{has_unsubscribe}}
- link_count: {{link_count}}
- image_count: {{image_count}}
- body_length_chars: {{body_length_chars}}

Body (plain text):
{{body_text}}

---

Parse this enquiry into the structured fields defined by the response schema. Required fields must be filled; optional fields should be null (or an empty array for lists) when the email doesn't say.`

/**
 * Build a JSON schema from the user-defined field list. The shape matches the
 * `text.format.json_schema` payload that `runEmailAiTool` forwards to the
 * OpenAI Responses API. All fields are listed in `required` and non-required
 * fields are made nullable — that's the OpenAI-recommended way to "allow
 * missing" while keeping strict-mode validation.
 */
/**
 * Build a single property entry for one signal field. Centralises the
 * boolean / enum-string / integer / free-form string dispatch so all three
 * signal blocks (client / date / group) stay in lockstep.
 */
const buildSignalProperty = (signal) => {
	const description = (signal.description || "").trim()

	if (signal.type === "boolean") {
		return { type: ["boolean", "null"], description }
	}
	if (signal.type === "integer") {
		return { type: ["integer", "null"], description }
	}
	if (signal.type === "number") {
		return { type: ["number", "null"], description }
	}
	if (signal.enum) {
		// Enum-constrained string — null is included in the enum list so the
		// field can be omitted under strict-mode validation.
		return {
			type: ["string", "null"],
			enum: [...signal.enum, null],
			description,
		}
	}
	// Free-form nullable string fallback.
	return { type: ["string", "null"], description }
}

/**
 * Wrap a list of signal field defs into a strict-mode JSON schema object
 * with every key in `required`.
 */
const buildSignalObject = (signalFields, description) => {
	const properties = {}
	for (const signal of signalFields) {
		properties[signal.key] = buildSignalProperty(signal)
	}
	return {
		type: "object",
		additionalProperties: false,
		description,
		properties,
		required: Object.keys(properties),
	}
}

/**
 * Mirror of buildSignalObject for the live JSON preview. Returns the default
 * value for each field type — null for nullable scalars, false for booleans.
 */
const buildSignalPreview = (signalFields) => {
	const preview = {}
	for (const signal of signalFields) {
		preview[signal.key] = signal.type === "boolean" ? false : null
	}
	return preview
}

export const buildSchemaFromFields = (fields, predictions = []) => {
	const properties = {}

	for (const field of fields) {
		if (!field.key) continue

		let jsonType
		switch (field.type) {
			case "integer":
				jsonType = "integer"
				break
			case "number":
				jsonType = "number"
				break
			case "boolean":
				jsonType = "boolean"
				break
			case "array_string":
				jsonType = "array"
				break
			case "date":
			case "string":
			default:
				jsonType = "string"
		}

		const property = {}

		if (jsonType === "array") {
			property.type = field.required ? "array" : ["array", "null"]
			property.items = { type: "string" }
		} else {
			property.type = field.required ? jsonType : [jsonType, "null"]
		}

		const description = (field.description || "").trim()
		if (description) property.description = description
		if (field.type === "date") {
			property.description = `${description ? description + " " : ""}Use ISO YYYY-MM-DD format.`.trim()
		}

		properties[field.key] = property
	}

	// UTM tracking is a fixed nested object — every key always present, each
	// value nullable. Inserted before `predictions` so the response order reads
	// "extracted facts → tracking → judgement scores".
	const utmProperties = {}
	for (const utm of UTM_KEYS) {
		utmProperties[utm.key] = {
			type: ["string", "null"],
			description: `Value of the ${utm.key} parameter found in the email body, or null if not present.`,
		}
	}
	properties.utm_tracking = {
		type: "object",
		additionalProperties: false,
		description:
			"Standard UTM parameters extracted from any URL or text in the email body. All keys present; missing values are null.",
		properties: utmProperties,
		required: Object.keys(utmProperties),
	}

	// Client / date / group signal blocks all share the same per-field shape
	// (boolean | enum-string | integer | free-form string). Build them through
	// a single helper so the dispatch logic only lives in one place.
	properties.client_signals = buildSignalObject(
		CLIENT_SIGNAL_FIELDS,
		"Standardised, low-cardinality device/browser signals parsed from any User-Agent string or hint in the email. Built for aggregate reporting — values are constrained to fixed enums so they're stable to group on."
	)

	properties.date_signals = buildSignalObject(
		DATE_SIGNAL_FIELDS,
		"Travel-timing signals derived from the extracted dates and the email's received timestamp. Use these for booking-window triage and seasonality reporting."
	)

	properties.group_signals = buildSignalObject(
		GROUP_SIGNAL_FIELDS,
		"Composition / occasion / decision-making signals about the group enquiring. Drives sales pitch routing — a stag group is sold to very differently from a serious society or a corporate event."
	)

	if (predictions && predictions.length > 0) {
		const predictionProperties = {}
		for (const prediction of predictions) {
			if (!prediction.key) continue
			predictionProperties[prediction.key] = {
				type: "number",
				minimum: 0,
				maximum: 1,
				description: (prediction.description || "").trim(),
			}
		}

		properties.predictions = {
			type: "object",
			additionalProperties: false,
			description:
				"Model-estimated 0–1 scores for triage signals. Each prediction is independent and should be calibrated against its own description.",
			properties: predictionProperties,
			required: Object.keys(predictionProperties),
		}
	}

	return {
		type: "object",
		additionalProperties: false,
		properties,
		required: Object.keys(properties),
	}
}

/**
 * Build a sample / shape preview object from the field list. The values
 * illustrate the expected type rather than realistic data — the goal is to
 * show developers what JSON shape the model is being asked to return.
 */
export const buildPreviewFromFields = (fields, predictions = []) => {
	const preview = {}

	for (const field of fields) {
		if (!field.key) continue

		switch (field.type) {
			case "integer":
				preview[field.key] = field.required ? 0 : null
				break
			case "number":
				preview[field.key] = field.required ? 0 : null
				break
			case "boolean":
				preview[field.key] = field.required ? false : null
				break
			case "array_string":
				preview[field.key] = []
				break
			case "date":
				preview[field.key] = field.required ? "YYYY-MM-DD" : null
				break
			case "string":
			default:
				preview[field.key] = field.required ? "" : null
		}
	}

	const utmPreview = {}
	for (const utm of UTM_KEYS) {
		utmPreview[utm.key] = null
	}
	preview.utm_tracking = utmPreview

	preview.client_signals = buildSignalPreview(CLIENT_SIGNAL_FIELDS)
	preview.date_signals = buildSignalPreview(DATE_SIGNAL_FIELDS)
	preview.group_signals = buildSignalPreview(GROUP_SIGNAL_FIELDS)

	if (predictions && predictions.length > 0) {
		const predictionPreview = {}
		for (const prediction of predictions) {
			if (!prediction.key) continue
			predictionPreview[prediction.key] = 0
		}
		preview.predictions = predictionPreview
	}

	return preview
}

export const localStorageKey = (field) => `enquiry-ingest:${field}`
